import escapeHtml from "escape-html";

/**
 * Shared File Upload utils
 */
let errorCount = 0;

export const UploadErrorMessages = Object.freeze({
  EXTENSION: { localeKey: "fds.fileUpload.error.extension", defaultMessage: "The selected file must be a {0}" },
  MAX_SIZE: { localeKey: "fds.fileUpload.error.maxSize", defaultMessage: "The selected file must be smaller than {0}" },
  VIRUS: { localeKey: "fds.fileUpload.error.virus", defaultMessage: "The selected file contains a virus" },
  UPLOAD: { localeKey: "fds.fileUpload.error.upload", defaultMessage: "The selected file could not be uploaded – try again" },
});

export const UploadErrorTypes = Object.freeze({
  EXTENSION: "EXTENSION_NOT_ALLOWED",
  MAX_SIZE: "MAX_FILE_SIZE_EXCEEDED",
  VIRUS: "VIRUS_FOUND_IN_FILE",
  SERVER: "INTERNAL_SERVER_ERROR",
});

/**
 * Remove errors that are no longer needed
 * @param {Element} container - The file upload container element
 * @param {string} errorId - The file upload input id
 */
export const removeContainerErrors = (container, errorId) => {
  if (container.querySelector(`#${errorId}-error`)) {
    container.querySelector(`#${errorId}-error`).remove();
    container.classList.remove("govuk-form-group--error");
    container.querySelector(".fds-file-upload-dropzone__link-error").textContent = "";
  }
};

/**
 * Builds a valid, unique id for a file upload error message. HTML ids must not contain whitespace
 * (https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Global_attributes/id#syntax), and the
 * previous scheme derived the id from the filename, which could contain whitespace and could repeat.
 * An incrementing count is used instead, so the id is always valid and unique whatever the filename.
 * @param {string} fileInputId - The file upload input id, used as a stable prefix
 * @returns {string} A whitespace-free, unique error message id
 */
export const buildFileErrorId = (fileInputId) => {
  errorCount += 1;

  return `${fileInputId}-${errorCount}-error`;
};

/**
 * Finds the correct DOM error element and inserts error text into it
 * @param {Element} element - The file error element
 * @param {string} message - The error message string
 */
export const showError = (element, message) => {
  const errorHtml = `<span class="govuk-visually-hidden">Error:</span>`;
  element.querySelector(".fds-file-upload-item__error").innerHTML = `${errorHtml} ${escapeHtml(message)}`;
};

/**
 * Adds or removes the "Error: " prefix on the document title to reflect whether the page currently
 * contains any validation errors, following the GOV.UK error page-title pattern
 * (https://design-system.service.gov.uk/components/error-message/). File upload errors are added and
 * removed client side without a page reload, so the server-rendered title is kept in sync here.
 *
 * The prefix is deliberately not translated - it is always "Error:", matching the prefix that
 * generic.ftl renders server side, so the two never disagree about what to add or strip.
 *
 * Scans the whole document for inline error messages and for an error summary - this covers other file
 * uploads and any non-file-upload widgets that already show an error. The summary has to be included
 * because resolving an upload error client side removes the inline message but leaves a server rendered
 * summary on the page, and the title must not claim the page is error free while that is still shown.
 * The prefix is only added or removed when needed, so the function is safe to call on every upload
 * (success or failure).
 */
export const updatePageTitleErrorState = () => {
  const prefix = "Error: ";
  const hasErrors = document.querySelectorAll(".govuk-error-message, .govuk-error-summary").length > 0;
  const hasPrefix = document.title.startsWith(prefix);

  if (hasErrors && !hasPrefix) {
    document.title = `${prefix}${document.title}`;
  } else if (!hasErrors && hasPrefix) {
    document.title = document.title.slice(prefix.length);
  }
};
